import type {
  AssessmentHistorySelectDto,
  MRLAssessmentHistoryItem,
  TRLAssessmentHistoryItem,
  TrlTimelineByUseDomainItem,
} from '@@types/api.types';
import { AssessmentType } from '@@types/api.types';
import { formatDateUtc, formatMonthYearUtc } from '@utils/date.util';
import React from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { EmptyStateContainer } from './empty-state-container.component';

type AssessmentHistoryChartComponentProps = {
  assessmentType: AssessmentType;
  historyData?: AssessmentHistorySelectDto;
  onDataPointClick?: (assessmentId: string | null) => void;
  selectedUseDomain?: {
    domainId: string;
    useId: string;
  };
};

type TimelineItem = {
  assessmentId?: string;
  date: string;
  score: number;
};

type MultiLineChartDataPoint = {
  dateKey: string;
  [key: string]: number | string | null;
};

type SelectedUseDomain = {
  domainId: string;
  useId: string;
};

type TimelineSeries = {
  color: string;
  dataKey: string;
  domainId?: string;
  domainName?: string;
  latestScore?: number;
  lineLabel: string;
  timeline: TimelineItem[];
  useId?: string;
  useName?: string;
};

type TooltipPayloadEntry = {
  color: string;
  dataKey?: string;
  payload: MultiLineChartDataPoint;
  value?: number | null;
};

type ChartHoverState = {
  activeCoordinate?: { x?: number };
  activePayload?: TooltipPayloadEntry[];
  isTooltipActive?: boolean;
};

type DotRendererProps = {
  cx?: number;
  cy?: number;
  payload?: MultiLineChartDataPoint;
};

const COLORS = [
  '#3D2EFF',
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#FFA07A',
  '#98D8C8',
  '#F7DC6F',
  '#BB8FCE',
  '#85C1E9',
  '#F8C471',
];

const TRL_LEGACY_LABEL = 'TRL';
const MRL_LABEL = 'MRL';

function toTimelineItems(items: TRLAssessmentHistoryItem[] | MRLAssessmentHistoryItem[] | undefined): TimelineItem[] {
  return items?.map((item) => ({ assessmentId: item.assessmentId, date: item.date, score: item.score })) ?? [];
}

function getLatestScore(timeline: TimelineItem[]): number | undefined {
  const latest = timeline.reduce<TimelineItem | undefined>((currentLatest, item) => {
    if (!currentLatest) return item;
    return item.date.localeCompare(currentLatest.date) > 0 ? item : currentLatest;
  }, undefined);

  return latest?.score;
}

function buildTimelineSeriesFromLegacy(timeline: TimelineItem[], label: string): TimelineSeries[] {
  if (timeline.length === 0) return [];
  const latestScore = getLatestScore(timeline);
  return [
    {
      color: COLORS[0],
      dataKey: 'score',
      latestScore,
      lineLabel: label,
      timeline,
    },
  ];
}

function buildTimelineSeriesFromUseDomain(params: {
  selectedUseDomain?: SelectedUseDomain;
  trlTimelineByUseDomain: TrlTimelineByUseDomainItem[];
}): TimelineSeries[] {
  const { selectedUseDomain, trlTimelineByUseDomain } = params;
  const filtered = selectedUseDomain
    ? trlTimelineByUseDomain.filter(
        (item) => item.domainId === selectedUseDomain.domainId && item.useId === selectedUseDomain.useId,
      )
    : trlTimelineByUseDomain;

  return filtered.map((item, index) => {
    const timeline = toTimelineItems(item.timeline);
    const latestScore = getLatestScore(timeline);
    return {
      color: COLORS[index % COLORS.length],
      dataKey: `${item.useId}:${item.domainId}`,
      domainId: item.domainId,
      domainName: item.domainName,
      latestScore,
      lineLabel: `${item.useName} • ${item.domainName}`,
      timeline,
      useId: item.useId,
      useName: item.useName,
    };
  });
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function toDateOnlyKey(dateKey: string): string {
  return dateKey.slice(0, 10);
}

function toUtcDayDateKey(dateKey: string): string {
  return `${toDateOnlyKey(dateKey)}T00:00:00.000Z`;
}

function getCurrentUtcDayDateKey(): string {
  return toUtcDayDateKey(new Date().toISOString());
}

function sortTimelineByDate(timeline: TimelineItem[]): TimelineItem[] {
  return timeline.slice().sort((itemA, itemB) => itemA.date.localeCompare(itemB.date));
}

function toSortedTimelineByUtcDay(timeline: TimelineItem[]): TimelineItem[] {
  const latestPointByDay = new Map<string, TimelineItem>();
  sortTimelineByDate(timeline).forEach((item) => {
    latestPointByDay.set(toUtcDayDateKey(item.date), item);
  });

  return Array.from(latestPointByDay.entries())
    .sort(([dayA], [dayB]) => dayA.localeCompare(dayB))
    .map(([, item]) => item);
}

function buildMultiLineChartDataWithMonthlyCarryForward(series: TimelineSeries[]): MultiLineChartDataPoint[] {
  const allTimelineItems = series.flatMap((item) => item.timeline);
  if (allTimelineItems.length === 0) return [];
  const currentDateKey = getCurrentUtcDayDateKey();
  const existingDateOnlyKeys = new Set(allTimelineItems.map((item) => toDateOnlyKey(item.date)));
  const shouldAddCurrentDate = !existingDateOnlyKeys.has(toDateOnlyKey(currentDateKey));
  const dateKeys = Array.from(
    new Set([
      ...allTimelineItems.map((item) => toUtcDayDateKey(item.date)),
      ...(shouldAddCurrentDate ? [currentDateKey] : []),
    ]),
  ).sort((a, b) => a.localeCompare(b));

  const dataMap = new Map<string, MultiLineChartDataPoint>(
    dateKeys.map((dateKey) => [
      dateKey,
      {
        dateKey,
      },
    ]),
  );

  series.forEach((line) => {
    const sortedTimelineByDay = toSortedTimelineByUtcDay(line.timeline).filter((item) => isFiniteNumber(item.score));

    let timelineIndex = 0;
    let latestAssessmentId: string | null = null;
    let latestScore: number | null = null;
    dateKeys.forEach((dateKey) => {
      const dataPoint = dataMap.get(dateKey);
      if (!dataPoint) return;

      while (
        timelineIndex < sortedTimelineByDay.length &&
        toUtcDayDateKey(sortedTimelineByDay[timelineIndex].date).localeCompare(dateKey) <= 0
      ) {
        const timelinePoint = sortedTimelineByDay[timelineIndex];
        latestAssessmentId = timelinePoint.assessmentId ?? null;
        latestScore = timelinePoint.score;
        timelineIndex += 1;
      }

      dataPoint[line.dataKey] = latestScore;
      dataPoint[`${line.dataKey}AssessmentId`] = latestAssessmentId;
    });
  });

  return Array.from(dataMap.values()).sort((itemA, itemB) => itemA.dateKey.localeCompare(itemB.dateKey));
}

function buildMultiLineChartDataWithTrlChangePoints(series: TimelineSeries[]): MultiLineChartDataPoint[] {
  const dateMap = new Map<string, MultiLineChartDataPoint>();
  const currentDateKey = getCurrentUtcDayDateKey();
  const currentDateOnlyKey = toDateOnlyKey(currentDateKey);

  series.forEach((line) => {
    const sortedTimeline = sortTimelineByDate(line.timeline);
    if (sortedTimeline.length === 0) return;

    const sortedTimelineByDay = toSortedTimelineByUtcDay(sortedTimeline);

    const changePoints: { assessmentId: string | null; date: string; score: number }[] = [];
    let previousScore: number | null = null;

    sortedTimelineByDay.forEach((item) => {
      if (!isFiniteNumber(item.score)) return;
      const dayDateKey = toUtcDayDateKey(item.date);

      if (previousScore === null || item.score !== previousScore) {
        changePoints.push({
          assessmentId: item.assessmentId ?? null,
          date: dayDateKey,
          score: item.score,
        });
        previousScore = item.score;
      }
    });

    if (changePoints.length === 0) return;
    const latestPoint = changePoints[changePoints.length - 1];
    if (!latestPoint) return;

    if (toDateOnlyKey(latestPoint.date) !== currentDateOnlyKey && latestPoint.date.localeCompare(currentDateKey) < 0) {
      changePoints.push({
        assessmentId: latestPoint.assessmentId,
        date: currentDateKey,
        score: latestPoint.score,
      });
    }

    changePoints.forEach((point) => {
      const existing = dateMap.get(point.date);
      const dataPoint = existing ?? { dateKey: point.date };
      dataPoint[line.dataKey] = point.score;
      dataPoint[`${line.dataKey}AssessmentId`] = point.assessmentId;
      dateMap.set(point.date, dataPoint);
    });
  });

  return Array.from(dateMap.values()).sort((itemA, itemB) => itemA.dateKey.localeCompare(itemB.dateKey));
}

function renderClickableDot(params: {
  color: string;
  dataKey: string;
  dotProps: DotRendererProps;
  isActive: boolean;
  onDataPointClick?: (assessmentId: string | null) => void;
}): React.ReactElement<SVGElement> {
  const { color, dataKey, dotProps, isActive, onDataPointClick } = params;
  const { cx, cy, payload } = dotProps;
  if (typeof cx !== 'number' || typeof cy !== 'number' || !payload) return <g />;

  const assessmentId = getAssessmentIdFromPoint({
    dataKey,
    payload,
  });

  return (
    <circle
      cx={cx}
      cy={cy}
      r={isActive ? 6 : 4}
      fill={isActive ? color : 'white'}
      stroke={isActive ? 'white' : color}
      strokeWidth={2}
      style={{ cursor: assessmentId !== undefined ? 'pointer' : 'default' }}
      onClick={() => {
        if (!onDataPointClick || assessmentId === undefined) return;
        onDataPointClick(assessmentId);
      }}
    />
  );
}

function getAssessmentIdFromPoint(params: {
  dataKey?: string;
  payload?: MultiLineChartDataPoint;
}): string | null | undefined {
  const { dataKey, payload } = params;
  if (!dataKey || !payload) return undefined;
  const assessmentIdValue = payload[`${dataKey}AssessmentId`];
  if (assessmentIdValue === null) return null;
  if (typeof assessmentIdValue === 'string') return assessmentIdValue;
  return undefined;
}

type LegendProps = {
  series: TimelineSeries[];
  typeLabel: 'MRL' | 'TRL';
};

const Legend: React.FC<LegendProps> = (props) => {
  const { series, typeLabel } = props;

  if (series.length <= 1) return null;

  return (
    <div className="w-64 shrink-0 pr-1">
      <div className="max-h-[220px] overflow-auto space-y-2 text-xs text-gray-700">
        {series.map((item) => {
          const hasUseDomain = Boolean(item.useName && item.domainName);
          const scoreLabel = `${typeLabel} ${item.latestScore ?? '—'}`;

          return (
            <div
              className="flex items-start gap-2"
              key={item.dataKey}
            >
              <div
                className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <div className="min-w-0">
                {hasUseDomain ? (
                  <div className="leading-4 wrap-break-word">
                    <span className="font-medium">{item.useName}</span>
                    <span className="mx-1 text-gray-400">•</span>
                    <span>{item.domainName}</span>
                    <span className="ml-1 text-gray-500">({scoreLabel})</span>
                  </div>
                ) : (
                  <div className="leading-4 wrap-break-word">
                    {item.lineLabel} <span className="text-gray-500">({scoreLabel})</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

type TooltipContentProps = {
  payload: TooltipPayloadEntry[];
  seriesByKey: Map<string, TimelineSeries>;
  typeLabel: 'MRL' | 'TRL';
};

const TooltipContent: React.FC<TooltipContentProps> = (props) => {
  const { payload, seriesByKey, typeLabel } = props;

  const data = payload[0]?.payload;
  const formattedDate = data ? formatDateUtc(data.dateKey) : null;

  if (!data || !formattedDate) return null;

  const validPayload = payload.filter((entry) => {
    if (!entry.dataKey) return false;
    return isFiniteNumber(entry.value);
  });
  if (validPayload.length === 0) return null;

  return (
    <div className="flex flex-col gap-y-1 rounded border border-gray-200 bg-white p-2 shadow-sm">
      <div className="font-medium text-gray-700">{formattedDate}</div>
      {validPayload.map((entry) => {
        if (!entry.dataKey || !isFiniteNumber(entry.value)) return null;
        const series = seriesByKey.get(entry.dataKey);
        if (!series) return null;

        const scoreLabel = `${typeLabel} ${entry.value}`;
        const hasUseDomain = Boolean(series.useName && series.domainName);

        return (
          <div
            className="flex items-center gap-2"
            key={series.dataKey}
          >
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />

            {hasUseDomain ? (
              <div className="text-gray-700 whitespace-nowrap">
                <span className="font-medium">{series.useName}</span>
                <span className="mx-1 text-gray-400">•</span>
                <span>{series.domainName}</span>
                <span className="ml-1 text-gray-500">({scoreLabel})</span>
              </div>
            ) : (
              <div className="text-gray-700 whitespace-nowrap">
                <span className="font-medium">{series.lineLabel}</span>{' '}
                <span className="text-gray-500">({scoreLabel})</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

type TooltipRenderProps = {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
};

const renderTooltipContent = (params: {
  seriesByKey: Map<string, TimelineSeries>;
  typeLabel: 'MRL' | 'TRL';
  tooltipProps: TooltipRenderProps;
}): React.ReactNode => {
  const {
    seriesByKey,
    typeLabel,
    tooltipProps: { active, payload },
  } = params;

  if (!active || !payload || payload.length === 0) return null;

  return (
    <TooltipContent
      payload={payload}
      seriesByKey={seriesByKey}
      typeLabel={typeLabel}
    />
  );
};

export const AssessmentHistoryChartComponent: React.FC<AssessmentHistoryChartComponentProps> = (props) => {
  const { assessmentType, historyData, onDataPointClick, selectedUseDomain } = props;
  const [hoverPayload, setHoverPayload] = React.useState<TooltipPayloadEntry[] | null>(null);
  const [hoverX, setHoverX] = React.useState<number | null>(null);

  const isTrlType = assessmentType === AssessmentType.TRL;
  const typeLabel: 'MRL' | 'TRL' = isTrlType ? 'TRL' : 'MRL';

  const trlSeries = buildTimelineSeriesFromUseDomain({
    selectedUseDomain,
    trlTimelineByUseDomain: historyData?.trlTimelineByUseDomain ?? [],
  });
  const trlFallbackSeries = buildTimelineSeriesFromLegacy(toTimelineItems(historyData?.trlTimeline), TRL_LEGACY_LABEL);
  const mrlSeries = buildTimelineSeriesFromLegacy(toTimelineItems(historyData?.mrlTimeline), MRL_LABEL);

  const series = isTrlType ? (trlSeries.length > 0 ? trlSeries : trlFallbackSeries) : mrlSeries;
  const isTrlOverview = isTrlType && !selectedUseDomain && series.length > 1;
  const chartData = isTrlType
    ? buildMultiLineChartDataWithTrlChangePoints(series)
    : buildMultiLineChartDataWithMonthlyCarryForward(series);

  const seriesByKey = React.useMemo(() => {
    return new Map(series.map((item) => [item.dataKey, item]));
  }, [series]);

  if (chartData.length === 0) {
    return <EmptyStateContainer title={isTrlType ? 'No TRL Data Available' : 'No MRL Data Available'} />;
  }

  const handleChartHover = (state: ChartHoverState): void => {
    if (!state.isTooltipActive || !state.activePayload || state.activePayload.length === 0) {
      setHoverPayload(null);
      setHoverX(null);
      return;
    }

    setHoverPayload(state.activePayload);
    const nextHoverX = state.activeCoordinate?.x;
    setHoverX(typeof nextHoverX === 'number' ? nextHoverX : null);
  };

  return (
    <div className="flex w-full gap-4">
      <div className="relative min-w-0 flex-1">
        <div className="h-[220px]">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              onMouseLeave={() => {
                setHoverPayload(null);
                setHoverX(null);
              }}
              onMouseMove={handleChartHover}
              style={{ cursor: 'default' }}
            >
              <XAxis
                dataKey="dateKey"
                tick={{ fill: '#666' }}
                tickFormatter={formatMonthYearUtc}
                axisLine={{ stroke: '#eee' }}
              />
              <YAxis
                domain={[0, 9]}
                ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
                tick={{ fill: '#666' }}
                axisLine={{ stroke: '#eee' }}
              />
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#eee"
              />
              <Tooltip content={() => null} />
              {series.map((line) => (
                <Line
                  key={line.dataKey}
                  type="monotone"
                  dataKey={line.dataKey}
                  stroke={line.color}
                  strokeOpacity={isTrlOverview ? 0.5 : 1}
                  strokeWidth={2}
                  dot={(dotProps) => {
                    return renderClickableDot({
                      color: line.color,
                      dataKey: line.dataKey,
                      dotProps,
                      isActive: false,
                      onDataPointClick,
                    });
                  }}
                  activeDot={(dotProps) => {
                    return renderClickableDot({
                      color: line.color,
                      dataKey: line.dataKey,
                      dotProps,
                      isActive: true,
                      onDataPointClick,
                    });
                  }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {hoverPayload && hoverPayload.length > 0 && hoverX !== null && (
          <div
            className="pointer-events-none absolute top-full z-10 mt-2"
            style={{
              left: hoverX,
              transform: 'translateX(-50%)',
            }}
          >
            {renderTooltipContent({
              seriesByKey,
              typeLabel,
              tooltipProps: { active: true, payload: hoverPayload },
            })}
          </div>
        )}
      </div>

      <Legend
        series={series}
        typeLabel={typeLabel}
      />
    </div>
  );
};
